import { CircleSize, IconSize } from "./LayoutProvider";

import { AHTeamIcon, AHTeamIconRelay } from "./AHTeamIcon";
import { Circle } from "./AHTheme/Circle";
import React from "react";
import { TeamIcon } from "../globalTypes";
import { graphql, useFragment } from "react-relay";
import { AHTeamIconCircle$key } from "./__generated__/AHTeamIconCircle.graphql";
import { Box } from "./AHTheme/Box";
import { AHIcon } from "./AHIcon";
import { ColorType } from "arkh";
export type AHTeamIconCircleSize = "small" | "smallMedium" | "medium" | "large";
export type AHTeamIconType = TeamIcon | null | keyof typeof TeamIcon;
type Props = {
  color: string | null;
  iconColor?: ColorType;
  type: AHTeamIconType;
  size?: AHTeamIconCircleSize;
  isPrivate?: boolean;
};

const sizeMapping: Record<
  AHTeamIconCircleSize,
  { icon: IconSize; circle: CircleSize }
> = {
  large: {
    icon: "xlarge",
    circle: "xlarge",
  },
  medium: {
    icon: "medium",
    circle: "medium",
  },
  smallMedium: {
    icon: "small",
    circle: "smallMedium",
  },
  small: {
    icon: "xsmall",
    circle: "small",
  },
};

export function TeamLockedIcon(props: { size: AHTeamIconCircleSize }) {
  const sizeMapping: Record<
    AHTeamIconCircleSize,
    { icon: IconSize; circle: CircleSize; offset: number }
  > = {
    small: {
      icon: "xxsmall",
      circle: "xsmall",
      offset: -5,
    },
    smallMedium: {
      icon: "xxsmall",
      circle: "small",
      offset: -6,
    },
    medium: {
      icon: "xsmall",
      circle: "smallMedium",
      offset: -10,
    },
    large: {
      icon: "xsmall",
      circle: "smallMedium",
      offset: -6,
    },
  };

  return (
    <Box
      style={{
        position: "absolute",
        right: sizeMapping[props.size].offset,
        top: sizeMapping[props.size].offset,
      }}
    >
      <Circle
        size={sizeMapping[props.size].circle}
        color="surface-primary"
        borderColor="border-primary"
        borderWidth={1}
        style={{
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <AHIcon
          type="locked"
          color="text-secondary"
          size={sizeMapping[props.size].icon}
        />
      </Circle>
    </Box>
  );
}

export function AHTeamIconCircle(props: Props) {
  return (
    <Box>
      <Circle
        size={sizeMapping[props.size ?? "medium"].circle}
        color="primary"
        style={props.color ? { backgroundColor: props.color } : undefined}
      >
        <AHTeamIcon
          size={sizeMapping[props.size ?? "medium"].icon}
          color={props.iconColor ?? "lightText"}
          type={props.type ?? TeamIcon.default}
        />
      </Circle>
      {props.isPrivate ? (
        <TeamLockedIcon size={props.size ?? "medium"} />
      ) : null}
    </Box>
  );
}

export function AHTeamIconCircleRelay(props: {
  size?: AHTeamIconCircleSize;
  team: AHTeamIconCircle$key;
}) {
  const team = useFragment(
    graphql`
      fragment AHTeamIconCircle on Team {
        color
        ...AHTeamIcon
        isPrivate
      }
    `,
    props.team
  );

  return (
    <Box>
      <Circle
        size={sizeMapping[props.size ?? "medium"].circle}
        color="primary"
        style={team.color ? { backgroundColor: team.color } : undefined}
      >
        <AHTeamIconRelay
          size={sizeMapping[props.size ?? "medium"].icon}
          color="lightText"
          team={team}
        />
      </Circle>
      {team.isPrivate ? <TeamLockedIcon size={props.size ?? "medium"} /> : null}
    </Box>
  );
}
